import React from 'react';
import { cn } from '@/lib/utils';

// Button variants
export const buttonVariants = {
  base: 'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none cursor-pointer',
  variants: {
    variant: {
      primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
      secondary: 'border-2 border-primary bg-surface text-foreground hover:bg-primary/80',
      accent: 'bg-accent text-accent-foreground hover:bg-accent/90',
      ghost: 'hover:bg-surface hover:text-foreground',
      destructive: 'bg-danger text-white hover:bg-danger/90',
      outline: 'border border-border bg-background hover:bg-surface hover:border-border',
      dark: 'bg-foreground text-background hover:bg-foreground/90 shadow-sm',
    },
    size: {
      sm: 'h-8 px-3 text-xs',
      md: 'h-10 px-4 py-2',
      lg: 'h-12 px-8',
      xl: 'h-14 px-10 text-lg',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
} as const;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof buttonVariants.variants.variant;
  size?: keyof typeof buttonVariants.variants.size;
  loading?: boolean;
  children: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant = 'primary', size = 'md', loading = false, children, disabled, ...props },
    ref,
  ) => {
    return (
      <button
        className={cn(
          buttonVariants.base,
          buttonVariants.variants.variant[variant],
          buttonVariants.variants.size[size],
          className,
        )}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : null}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';
